import {useTranslation} from 'react-i18next';

import {Box, Grid, Typography} from '@mui/material';

import {EmpionButton} from '../../../components/EmpionButton/EmpionButton';
import {useTrackingEvents} from '../../../hooks/useTrackingEvents';
import {orangeColors} from '../../../layouts/theme/designSystem';
import {neutral} from '../../../layouts/theme/designSystem';
import {RequiredLanguageProfile} from '../../../types/shared-types';
import {TrackingEvents} from '../../../types/TrackingEvents';
import {safelyUseLanguageProfile} from '../../../utils/safelyUseLanguageProfile';

interface ResultsHeroSectionProps {
  cultureTypeTitle: RequiredLanguageProfile;
  cultureTypeText: RequiredLanguageProfile;
  cultureTypeImageUrl: string;
}

export const ResultsHeroSection = ({
  cultureTypeTitle,
  cultureTypeText,
  cultureTypeImageUrl,
}: ResultsHeroSectionProps) => {
  const {t, i18n} = useTranslation();
  const currentLanguage = i18n.resolvedLanguage as 'en' | 'de';
  const {eventAttributes} = useTrackingEvents({
    version: TrackingEvents.Version.v1,
    category: TrackingEvents.Category.Marketing,
    action: TrackingEvents.Action.B2BBookAppointmentClicked,
    payload: {
      page: 'b2b_results',
      section: 'results_hero',
    },
  });
  const localizedCultureTypeTitle = safelyUseLanguageProfile(currentLanguage, cultureTypeTitle);
  const localizedCultureTypeText = safelyUseLanguageProfile(currentLanguage, cultureTypeText);

  return (
    <Grid
      width='100%'
      sx={{
        py: {xs: 4, md: 6},
        px: {xs: 2, md: 4},
        backgroundColor: '#fff',
      }}
    >
      <Typography
        sx={{
          color: neutral['primary-black'],
          fontWeight: 700,
          fontSize: {xs: '2rem', sm: '2.5rem', md: '4rem'},
          lineHeight: 1.15,
          textAlign: 'center',
        }}
      >
        {t('b2b.results.hero.headline')}
      </Typography>

      <Box
        sx={{
          mt: {xs: 2, md: 6},
          pb: {xs: 2, md: 6},
          backgroundColor: '#fff5e8',
          border: '1px solid #ffbf3e',
          borderRadius: '13px',
        }}
      >
        <Box
          sx={{
            mt: {xs: 3, md: 4},
            p: {xs: 2, md: 2.5},
            display: 'flex',
            gap: {xs: 2, md: 3},
            flexDirection: {xs: 'column', md: 'row'},
            alignItems: {xs: 'center', md: 'flex-start'},
          }}
        >
          <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0}}>
            <Box
              sx={{
                width: {xs: 78, md: 92},
                height: {xs: 78, md: 92},
                borderRadius: '50%',
                border: '2px solid #ffffff',
                boxShadow: '0px 3px 10px 0px rgba(0,0,0,0.12)',
                overflow: 'hidden',
                background: cultureTypeImageUrl
                  ? 'transparent'
                  : 'conic-gradient(#95bbc7 0deg 120deg, #c7dce2 120deg 240deg, #ff7d39 240deg 360deg)',
              }}
            >
              {cultureTypeImageUrl && (
                <Box
                  component='img'
                  src={cultureTypeImageUrl}
                  alt={localizedCultureTypeTitle}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              )}
            </Box>
            <Typography
              sx={{
                mt: 1,
                color: '#163438',
                fontWeight: 700,
                fontSize: {xs: '0.875rem', md: '1rem'},
                lineHeight: 1.3,
                textAlign: 'center',
              }}
            >
              {localizedCultureTypeTitle}
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
            }}
          >
            <Typography
              sx={{
                color: '#163438',
                fontWeight: 400,
                fontSize: {xs: '0.875rem', md: '1rem'},
                lineHeight: 1.5,
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {localizedCultureTypeText}
            </Typography>
          </Box>
        </Box>
        <EmpionButton
          onClick={() => document.getElementById('hubspot-widget')?.scrollIntoView({behavior: 'smooth'})}
          {...eventAttributes}
          sx={{
            px: {xs: 2.75, md: 3.5},
            py: {xs: 0.75, md: 0},
            width: {xs: '100%', sm: 'auto'},
            minHeight: '70px',
            maxWidth: {xs: '100%', sm: '640px'},
            display: 'flex',
            mx: 'auto',
            backgroundColor: orangeColors[900],
            '&:hover': {
              backgroundColor: orangeColors[950],
            },
          }}
        >
          {t('b2b.hero.bookingButton')}
        </EmpionButton>
      </Box>
    </Grid>
  );
};
